import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authStatus, authLogin, authLogout } from "@/lib/api";

const AuthContext = createContext(null);

// Permission helpers — single source of truth for what each role can do
const PERMISSIONS = {
  admin: {
    seeAmounts: true,
    seeHonorarios: true,
    editAll: true,
    editActaPhotos: true,
    deleteExpediente: true,
    createExpediente: true,
    downloadContract: true,
    downloadAllDocs: true,
    editTemplates: true,
    editProfile: true,
    managePins: true,
  },
  operativo: {
    seeAmounts: false,
    seeHonorarios: false,
    editAll: true,
    editActaPhotos: true,
    deleteExpediente: false,
    createExpediente: true,
    downloadContract: false,
    downloadAllDocs: true, // all except contrato
    editTemplates: true,
    editProfile: false,
    managePins: false,
  },
  campo: {
    seeAmounts: false,
    seeHonorarios: false,
    editAll: false,
    editActaPhotos: true,
    deleteExpediente: false,
    createExpediente: false,
    downloadContract: false,
    downloadAllDocs: false, // only Acta + photos
    editTemplates: false,
    editProfile: false,
    managePins: false,
  },
};

const DOCS_FOR_CAMPO = new Set(["acta_hitos", "fotos_linderos", "fotos_letrero"]);
const DOCS_ADMIN_ONLY = new Set(["contrato"]);

export function AuthProvider({ children }) {
  const [role, setRole] = useState(null);
  const [setupComplete, setSetupComplete] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);

  const refresh = useCallback(async (retry = true) => {
    setLoading(true);
    setNetworkError(false);
    try {
      const s = await authStatus();
      setSetupComplete(!!s.setup_complete);
      setRole(s.role || null);
    } catch (e) {
      console.warn("auth/status failed:", e);
      if (retry) {
        await new Promise((r) => setTimeout(r, 800));
        try {
          const s2 = await authStatus();
          setSetupComplete(!!s2.setup_complete);
          setRole(s2.role || null);
        } catch (err2) {
          console.error("auth/status failed on retry:", err2);
          setNetworkError(true);
          setSetupComplete(null);
          setRole(null);
        }
      } else {
        setNetworkError(true);
        setSetupComplete(null);
        setRole(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (r, pin) => {
    const data = await authLogin(r, pin);
    setRole(data.role);
    return data;
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch (err) {
      console.warn("Logout failed (continuing):", err);
    }
    setRole(null);
  };

  const can = (action) => {
    if (!role) return false;
    return !!PERMISSIONS[role]?.[action];
  };

  const canViewDoc = (docType) => {
    if (!role) return false;
    if (DOCS_ADMIN_ONLY.has(docType)) return role === "admin";
    if (role === "campo") return DOCS_FOR_CAMPO.has(docType);
    return true;
  };

  return (
    <AuthContext.Provider
      value={{ role, setupComplete, loading, networkError, login, logout, refresh, can, canViewDoc }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
